import React from 'react'
import styled, { keyframes } from 'styled-components'
import ShieldRoundedIcon          from '@mui/icons-material/ShieldRounded'
import SettingsRoundedIcon        from '@mui/icons-material/SettingsRounded'
import AccessTimeRoundedIcon      from '@mui/icons-material/AccessTimeRounded'
import LocalFireDepartmentRounded from '@mui/icons-material/LocalFireDepartmentRounded'
import WaterDropRoundedIcon       from '@mui/icons-material/WaterDropRounded'
import HelpOutlineRoundedIcon     from '@mui/icons-material/HelpOutlineRounded'
import { scoreColour, formatPercent } from './scoreColour'
import { line, radius, font, transition } from 'features/dashboard/tokens'

const DOMAIN_ICONS = {
  shield:   ShieldRoundedIcon,
  settings: SettingsRoundedIcon,
  clock:    AccessTimeRoundedIcon,
  alert:    LocalFireDepartmentRounded,
  water:    WaterDropRoundedIcon,
}

/**
 * One row per TaskDomain: coloured icon chip, name + cadence label,
 * animated horizontal bar (fills from 0 → score on mount), percentage on
 * the right. Synthetic "Unclassified" rows with no data are filtered out
 * so the panel never shows a ghost row.
 */
const StackedDomainList = ({ rows = [], onRowClick }) => {
  // Hide the synthetic 'unclassified' row when it carries no actual data
  // (no set_count, no total_tasks) — it's an artefact of bucketing and
  // renders as an empty grey bar that confuses the eye.
  const visible = rows.filter(r =>
    r.task_domain_id !== null ||
    (r.set_count || 0) > 0 ||
    (r.total_tasks || 0) > 0,
  )

  if (!visible.length) {
    return <Empty>No compliance sets configured yet.</Empty>
  }
  return (
    <Wrap>
      {visible.map((row, i) => {
        const colour = row.colour || scoreColour(row.score)
        const safeScore = row.score === null || row.score === undefined ? 0 : row.score
        const Icon = DOMAIN_ICONS[row.icon] || HelpOutlineRoundedIcon
        return (
          <Row key={row.id || row.name}
            onClick={() => onRowClick?.(row)}
            $clickable={!!onRowClick}
            $stagger={i}>
            <IconChip $colour={colour}>
              <Icon style={{ fontSize: 16 }} />
            </IconChip>
            <Body>
              <Head>
                <NameRow>
                  <Name>{row.short || row.name}</Name>
                  {row.freq_label ? <Meta>· {row.freq_label}</Meta> : null}
                </NameRow>
                {row.overdue > 0 && (
                  <OverdueTag>{row.overdue} overdue</OverdueTag>
                )}
              </Head>
              <BarTrack>
                <BarFill
                  $colour={colour}
                  style={{ width: `${Math.max(2, safeScore)}%`, ['--target-width']: `${Math.max(2, safeScore)}%` }}
                />
              </BarTrack>
            </Body>
            <Score $colour={scoreColour(row.score)}>{formatPercent(row.score)}</Score>
          </Row>
        )
      })}
    </Wrap>
  )
}

export default StackedDomainList

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`
const fillIn = keyframes`
  from { width: 0; }
  to   { width: var(--target-width); }
`

const Wrap = styled.div`
  display: flex; flex-direction: column;
`

const Empty = styled.div`
  padding: 18px;
  font-size: ${font.body};
  color: ${({ theme }) => theme.colors.grey[800]};
  background: ${({ theme }) => theme.colors.lighterGrey};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
`

const Row = styled.div`
  display: grid;
  grid-template-columns: 36px 1fr 56px;
  gap: 14px;
  align-items: center;
  padding: 10px 8px;
  border-radius: ${radius.md};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: ${transition};
  animation: ${fadeUp} 380ms ease both;
  animation-delay: ${({ $stagger }) => 40 + $stagger * 50}ms;

  ${({ $clickable }) => $clickable && `&:hover { background: rgba(51,190,204,0.06); }`}
`

const IconChip = styled.div`
  width: 34px; height: 34px;
  border-radius: ${radius.md};
  display: grid; place-items: center;
  background: ${({ $colour }) => `${$colour}1A`};
  color: ${({ $colour }) => $colour};
  border: 1px solid ${({ $colour }) => `${$colour}33`};
  transition: transform 160ms ease;
  ${Row}:hover & { transform: translateY(-1px) scale(1.06); }
`

const Body = styled.div`
  min-width: 0;
`

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: ${font.small};
  margin-bottom: 6px;
  gap: 8px;
`

const NameRow = styled.div`
  display: inline-flex; gap: 6px; align-items: baseline; min-width: 0;
`

const Name = styled.span`
  font-weight: 700;
  font-size: ${font.body};
  color: ${({ theme }) => theme.colors.haiti};
  letter-spacing: -0.1px;
`

const Meta = styled.span`
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: ${font.small};
`

const OverdueTag = styled.span`
  font-size: ${font.caption};
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #b00020;
  background: rgba(204, 34, 0, 0.10);
  border: 1px solid rgba(204, 34, 0, 0.20);
  padding: 1px 7px;
  border-radius: ${radius.pill};
  white-space: nowrap;
`

const BarTrack = styled.div`
  height: 10px;
  border-radius: ${radius.pill};
  background: rgba(12, 18, 41, 0.05);
  overflow: hidden;
  position: relative;
`

const BarFill = styled.div`
  height: 100%;
  border-radius: inherit;
  background: ${({ $colour }) => `${$colour}`};
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.18);
  animation: ${fillIn} 720ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
`

const Score = styled.div`
  text-align: right;
  font-weight: 800;
  font-size: ${font.body};
  color: ${({ $colour }) => $colour};
  font-variant-numeric: tabular-nums;
`
